import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { 
  TwitchIcon, YoutubeIcon, KickIcon, TikTokIcon, 
  FacebookIcon, InstagramIcon, XIcon, RumbleIcon, 
  TrovoIcon, AfreecaTVIcon, BilibiliIcon, DLiveIcon 
} from '@/components/icons/PlatformIcons';

const chatMessages = [
  { user: 'StreamFan42', message: 'Wow the view is amazing!', color: 'text-purple-400' },
  { user: 'NightOwl_', message: 'Where is this??', color: 'text-cyan-400' },
  { user: 'TravelVibes', message: 'This is so cool', color: 'text-green-400' },
  { user: 'ChillViewer', message: 'Love the quality', color: 'text-pink-400' },
  { user: 'WandererX', message: 'Stream is so smooth!', color: 'text-yellow-400' },
  { user: 'AdventureTime', message: 'No buffering at all', color: 'text-orange-400' },
  { user: 'ExplorerKai', message: 'Best IRL stream', color: 'text-blue-400' },
  { user: 'CityWalker', message: 'Connection is solid', color: 'text-red-400' },
];

export function Home() {
  const [visibleMessages, setVisibleMessages] = useState<number[]>([0, 1, 2]);
  const [messageIndex, setMessageIndex] = useState(3);
  const [viewerCount, setViewerCount] = useState(847);
  const [bitrate1, setBitrate1] = useState(5.2);
  const [bitrate2, setBitrate2] = useState(3.1);

  useEffect(() => {
    const chatInterval = setInterval(() => {
      setVisibleMessages(prev => {
        const newMessages = [...prev.slice(1), messageIndex];
        return newMessages;
      });
      setMessageIndex(prev => (prev + 1) % chatMessages.length);
    }, 2500);

    const viewerInterval = setInterval(() => {
      setViewerCount(prev => prev + Math.floor(Math.random() * 5) - 2);
    }, 3000);

    const bitrateInterval = setInterval(() => {
      setBitrate1(prev => Math.max(3, Math.min(8, prev + (Math.random() - 0.5) * 0.8)));
      setBitrate2(prev => Math.max(2, Math.min(5, prev + (Math.random() - 0.5) * 0.6)));
    }, 1500);

    return () => {
      clearInterval(chatInterval);
      clearInterval(viewerInterval);
      clearInterval(bitrateInterval);
    };
  }, [messageIndex]);

  return (
    <div className="min-h-screen">
      <SEO 
        canonical="/"
        description="Professional cloud OBS hosting for IRL streamers. 24/7 uptime, disconnect protection with BRB scenes, SRT/SRTLA/RIST relays. Compatible with Belabox, Moblin, IRLPro. Stream to Twitch, YouTube, Kick, TikTok & more."
      />
      {/* Hero Section - Full Width Camera POV */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Phone Frame */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Phone Body - Horizontal/Landscape */}
            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-[1.5rem] sm:rounded-[2.5rem] p-1 sm:p-2 shadow-2xl shadow-cyan-500/20 border border-gray-700/50">
              {/* Phone inner bezel */}
              <div className="relative bg-black rounded-[1.25rem] sm:rounded-[2rem] p-0.5 sm:p-1">
                {/* Side buttons - Hidden on mobile */}
                <div className="hidden sm:block absolute -top-1 left-24 w-12 h-1 bg-gray-700 rounded-b-sm" />
                <div className="hidden sm:block absolute -top-1 left-40 w-8 h-1 bg-gray-700 rounded-b-sm" />
                {/* Power button (bottom in landscape) */}
                <div className="hidden sm:block absolute -bottom-1 left-32 w-16 h-1 bg-gray-700 rounded-t-sm" />
                
                {/* Screen - 19:9 phone landscape aspect ratio */}
                <div className="relative bg-gray-950 rounded-[1rem] sm:rounded-[1.5rem] overflow-hidden" style={{ aspectRatio: '19/9' }}>
                  {/* Camera punch hole (left side in landscape) - Hidden on mobile */}
                  <div className="hidden sm:block absolute top-1/2 left-3 -translate-y-1/2 w-3 h-3 bg-gray-900 rounded-full border-2 border-gray-800 z-30" />
                  <div className="hidden sm:block absolute top-1/2 left-3.5 -translate-y-1/2 w-1.5 h-1.5 bg-gray-950 rounded-full z-30" />
                {/* Minimal gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-950" />

                {/* Main Hero Text - Centered */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10">
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-3xl sm:text-5xl md:text-8xl font-bold text-white tracking-tight"
                  >
                    Go Live.
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-2xl sm:text-4xl md:text-7xl font-bold text-cyan-400"
                  >
                    Anywhere.
                  </motion.p>
                </div>

                {/* Top Left - Live Indicator */}
                <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex items-center gap-1 sm:gap-2 z-20">
                  <div className="flex items-center gap-1 sm:gap-1.5 bg-red-500 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md">
                    <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white text-[10px] sm:text-xs font-bold">LIVE</span>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 bg-black/50 backdrop-blur px-2.5 py-1 rounded-md">
                    <Users className="w-3.5 h-3.5 text-white" />
                    <span className="text-white text-xs font-medium">{viewerCount.toLocaleString()}</span>
                  </div>
                </div>

                {/* Top Right - Bonding Connection Indicators - Hidden on small mobile */}
                <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col gap-1 sm:gap-1.5 z-20">
                  <motion.div 
                    animate={{ opacity: [0.8, 1, 0.8] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex items-center gap-1 sm:gap-1.5 bg-green-500/20 backdrop-blur border border-green-500/50 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md"
                  >
                    <div className="w-2 h-2 bg-green-400 rounded-full" />
                    <span className="text-green-400 text-[10px] sm:text-xs font-medium font-mono">{bitrate1.toFixed(1)} Mbps</span>
                    <span className="text-green-400/60 text-[10px] sm:text-xs hidden sm:inline">SIM1</span>
                  </motion.div>
                  <motion.div 
                    animate={{ opacity: [1, 0.8, 1] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className="flex items-center gap-1 sm:gap-1.5 bg-green-500/20 backdrop-blur border border-green-500/50 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md"
                  >
                    <div className="w-2 h-2 bg-green-400 rounded-full" />
                    <span className="text-green-400 text-[10px] sm:text-xs font-medium font-mono">{bitrate2.toFixed(1)} Mbps</span>
                    <span className="text-green-400/60 text-[10px] sm:text-xs hidden sm:inline">SIM2</span>
                  </motion.div>
                </div>

                {/* Bottom Left - Chat Messages - Hidden on mobile */}
                <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 w-48 sm:w-64 space-y-1 sm:space-y-1.5 z-20 hidden sm:block">
                  <AnimatePresence mode="popLayout">
                    {visibleMessages.map((msgIndex, i) => (
                      <motion.div
                        key={`${msgIndex}-${i}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className="bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5"
                      >
                        <span className={`text-xs font-semibold ${chatMessages[msgIndex].color}`}>
                          {chatMessages[msgIndex].user}
                        </span>
                        <span className="text-white text-xs ml-2">
                          {chatMessages[msgIndex].message}
                        </span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
              </div>
            </div>

            {/* Subtle glow */}
            <div className="absolute -inset-8 bg-cyan-500/5 rounded-full blur-3xl -z-10" />
          </motion.div>

          {/* CTA Buttons below the frame */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
          >
            <Link to="/pricing">
              <Button
                size="lg"
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-8 py-6 text-lg"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800 px-8 py-6 text-lg"
              >
                Join Discord
              </Button>
            </a>
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center text-gray-500 mt-6"
          >
            Professional IRL streaming infrastructure. Disconnect protection. Global servers.
          </motion.p>
        </div>
      </section>

      {/* Platform Logos - Scrolling Marquee */}
      <section className="py-12 px-4 border-t border-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-gray-500 text-sm uppercase tracking-wider mb-8">
            Stream to any platform
          </p>
          <div className="relative h-16 overflow-hidden">
            {/* Edge fade gradients */}
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0a0a0a] to-transparent pointer-events-none z-10" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0a0a0a] to-transparent pointer-events-none z-10" />
            
            {/* Marquee track */}
            <div className="flex animate-marquee">
              {[...Array(2)].map((_, setIndex) => (
                <div key={setIndex} className="flex">
                  {[
                    { Icon: TwitchIcon, name: 'Twitch' },
                    { Icon: YoutubeIcon, name: 'YouTube' },
                    { Icon: KickIcon, name: 'Kick' },
                    { Icon: TikTokIcon, name: 'TikTok*' },
                    { Icon: FacebookIcon, name: 'Facebook' },
                    { Icon: InstagramIcon, name: 'Instagram' },
                    { Icon: XIcon, name: 'X' },
                    { Icon: RumbleIcon, name: 'Rumble' },
                    { Icon: TrovoIcon, name: 'Trovo' },
                    { Icon: AfreecaTVIcon, name: 'AfreecaTV' },
                    { Icon: BilibiliIcon, name: 'Bilibili' },
                    { Icon: DLiveIcon, name: 'DLive' },
                  ].map((platform, i) => (
                    <div key={`${setIndex}-${i}`} className="flex flex-col items-center gap-2 flex-shrink-0 w-20">
                      <platform.Icon className="w-8 h-8 text-gray-400" />
                      <span className="text-xs text-gray-500">{platform.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Simple Value Props */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-4xl font-bold text-white mb-2">36+</div>
              <div className="text-gray-400">Server Locations</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="text-4xl font-bold text-white mb-2">99.9%</div>
              <div className="text-gray-400">Uptime</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="text-4xl font-bold text-white mb-2">&lt;24hr</div>
              <div className="text-gray-400">Deployment</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works - Redesigned */}
      <section className="py-24 px-4 bg-gradient-to-b from-transparent via-gray-900/50 to-transparent">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Three steps to go live
            </h2>
            <p className="text-gray-400 text-lg">No technical expertise required</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                title: 'Choose',
                description: 'Pick the plan that matches your streaming style. Basic to Pro, we have you covered.',
                accent: 'from-cyan-500 to-blue-500',
              },
              {
                step: '02',
                title: 'Connect',
                description: 'We set up your server within 24 hours. Point your streaming device at it and you\'re good to go.',
                accent: 'from-blue-500 to-purple-500',
              },
              {
                step: '03',
                title: 'Stream',
                description: 'Go live from anywhere. If you disconnect, viewers see a BRB screen instead of an offline error.',
                accent: 'from-purple-500 to-pink-500',
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity rounded-2xl" 
                     style={{ backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }} />
                <div className="relative bg-gray-900/80 border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-700 transition-colors">
                  <div className={`text-5xl font-bold bg-gradient-to-r ${item.accent} bg-clip-text text-transparent mb-4`}>
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 border-t border-gray-800/50">
        <div className="max-w-2xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white mb-6"
          >
            Ready to upgrade your streams?
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/pricing">
              <Button
                size="lg"
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-8 py-6 text-lg"
              >
                View Plans
              </Button>
            </Link>
            <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800 px-8 py-6 text-lg"
              >
                Join Discord
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
